import { events } from "@constants/tracking";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { getSubmittedRawInputValue } from "@memorang/helpers/user-submitted-answer";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	ResponsePrepTime,
	VideoPlayerConfig,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import AnswerShortQuestion from "@memorang/ui/components/StudySession/SessionContent/item-types/AnswerShortQuestion";
import DescribeImage from "@memorang/ui/components/StudySession/SessionContent/item-types/DescribeImage";
import ReadAloud from "@memorang/ui/components/StudySession/SessionContent/item-types/ReadAloud";
import RepeatSentence from "@memorang/ui/components/StudySession/SessionContent/item-types/RepeatSentence";
import ResponseToSituation from "@memorang/ui/components/StudySession/SessionContent/item-types/ResponseToSituation";
import RetellLecture from "@memorang/ui/components/StudySession/SessionContent/item-types/RetellLecture";
import SummarizeGroupDiscussion from "@memorang/ui/components/StudySession/SessionContent/item-types/SummarizeGroupDiscussion";
import { TimeOverrunBanner } from "@memorang/ui/components/StudySession/SessionContent/item-types/pte/TimeOverrunBanner";
import { trackCustomEvent } from "analytics";
import { memo, useCallback, useMemo, useState } from "react";
import FormativeTimeOverrunContainer from "../../../containers/FormativeTimeOverrunContainer";
import { extractAudioUri, extractVideoUri } from "../../../helpers/content";
import {
	getAllowAudioReplay,
	getAllowVideoReplay,
	getResponseTimeLimit,
	getSummativeResponseConfig,
	getVideoPlayerConfig,
	getVideoProgressSeekable,
} from "../../../helpers/response-timing";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import { useMarkResponseChangedIfGated } from "../../../hooks/useEnqueueAssessmentOnLeave";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import useResponseControlsStore from "../../../hooks/useResponseControlsStore";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

const captureError: (context: string, error: Error) => void = (
	_context,
	error,
) => {
	captureErrorToSentry(error, { error: serializeErrorForSentry(error) });
};

type RecorderProps = {
	audio: string;
	handleOnRecordingComplete: (uri: string | undefined) => void;
	captureError: (context: string, error: Error) => void;
	handleTracking: (status: "start" | "end") => void;
	disabled?: boolean;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowAudioReplay?: boolean;
	allowVideoReplay?: boolean;
	videoPlayerConfig?: VideoPlayerConfig;
	progressSeekable?: boolean;
	allowStopWhileRecording?: boolean;
	onResponseComplete: () => void;
	onAudioEnded: () => void;
	registerActiveResponseCompletion: (
		handler: () => Promise<void> | void,
	) => () => void;
	onLimitReachedChange: (reached: boolean) => void;
	onRecordingActiveChange: (active: boolean) => void;
	onPlaybackDurationChange: (durationMillis: number) => void;
};

type SpeakingItemInputProps = {
	itemType?: OriginalItemTypes;
	stem: string;
	instruction: string;
	audioUri: string;
	videoUri: string;
	images: { id: string; url: string }[];
	isSummativeTest?: boolean;
	recorderProps: RecorderProps;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: maps item types to input components
const SpeakingItemInput = memo(function SpeakingItemInput({
	itemType,
	stem,
	instruction,
	audioUri,
	videoUri,
	images,
	isSummativeTest,
	recorderProps,
}: SpeakingItemInputProps) {
	if (
		itemType === "RASW" ||
		itemType === "RASW_CORE" ||
		itemType === "PISW" ||
		itemType === "PISW_CORE"
	) {
		return (
			<ReadAloud stem={stem} instruction={instruction} {...recorderProps} />
		);
	}

	if (itemType === "ASQSW" || itemType === "ASQSW_CORE") {
		return (
			<AnswerShortQuestion
				instruction={instruction}
				audioUri={audioUri}
				{...recorderProps}
			/>
		);
	}

	if (itemType === "DISW" || itemType === "DISW_CORE") {
		return (
			<DescribeImage
				images={images}
				instruction={instruction}
				isSummativeTest={isSummativeTest}
				{...recorderProps}
			/>
		);
	}

	if (itemType === "RTSSW" || itemType === "RTSSW_CORE") {
		return (
			<ResponseToSituation
				instruction={instruction}
				audioUri={audioUri}
				stem={stem}
				{...recorderProps}
			/>
		);
	}

	if (
		itemType === "RLSW" ||
		itemType === "RSSW" ||
		itemType === "RSSW_CORE" ||
		itemType === "SGDL"
	) {
		const Component =
			itemType === "RLSW"
				? RetellLecture
				: itemType === "RSSW" || itemType === "RSSW_CORE"
					? RepeatSentence
					: SummarizeGroupDiscussion;
		return (
			<Component
				instruction={instruction}
				audioUri={audioUri}
				videoUri={videoUri}
				{...recorderProps}
			/>
		);
	}

	return null;
});

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

const SpeakingContainer = ({ item, isSplitLayout, isSummativeTest }: Props) => {
	const { content, response } = item;
	const itemId = item.id!;
	const itemType = item.metadata?.type;

	const { isAnswered } = useItemAnsweredState(item);

	const [recordingExpired, setRecordingExpired] = useState(false);
	const [expiredDismissed, setExpiredDismissed] = useState(false);
	const handleLimitReachedChange = useCallback((reached: boolean) => {
		setRecordingExpired(reached);
		if (!reached) {
			setExpiredDismissed(false);
		}
	}, []);

	const markResponseChangedIfGated = useMarkResponseChangedIfGated();
	const streaming = useAIFeedbackStore((s) => s.streaming);
	const userResponse = useAIFeedbackStore((s) => s.userResponse);
	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const setRecordingDuration = useAIFeedbackStore(
		(s) => s.setRecordingDuration,
	);
	const markResponseComplete = useResponseControlsStore(
		(s) => s.markResponseComplete,
	);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);
	const registerActiveResponseCompletion = useResponseControlsStore(
		(s) => s.registerActiveResponseCompletion,
	);
	const setRecordingActive = useResponseControlsStore(
		(s) => s.setRecordingActive,
	);

	const handleResponseComplete = useCallback(
		() => markResponseComplete(itemId),
		[markResponseComplete, itemId],
	);
	const handleAudioEnded = useCallback(
		() => markAudioComplete(itemId),
		[markAudioComplete, itemId],
	);
	const handleRegisterActiveResponseCompletion = useCallback(
		(handler: () => Promise<void> | void) =>
			registerActiveResponseCompletion(itemId, handler),
		[registerActiveResponseCompletion, itemId],
	);
	const handleRecordingActiveChange = useCallback(
		(active: boolean) => setRecordingActive(itemId, active),
		[setRecordingActive, itemId],
	);

	const storedResponse = userResponse?.[itemId];
	const audio =
		(typeof storedResponse === "string" ? storedResponse : undefined) ??
		(item.answer?.aiResponse?.answer || undefined) ??
		getSubmittedRawInputValue(item.answer, "audio") ??
		"";

	const handleOnRecordingComplete = useCallback(
		(uri: string | undefined) => {
			setUserResponse(itemId, uri);
			markResponseChangedIfGated(itemId, isSummativeTest, itemType);
		},
		[
			itemId,
			setUserResponse,
			markResponseChangedIfGated,
			isSummativeTest,
			itemType,
		],
	);

	const handlePlaybackDurationChange = useCallback(
		(durationMillis: number) => setRecordingDuration(itemId, durationMillis),
		[setRecordingDuration, itemId],
	);

	const handleTracking = useCallback(
		(status: "start" | "end") => {
			trackCustomEvent({
				eventName:
					status === "start"
						? events.sessionItemResponseAudioRecordingStarted
						: events.sessionItemResponseAudioRecordingEnded,
				itemId,
				status,
			});
		},
		[itemId],
	);

	const timeLimit = getResponseTimeLimit(response);
	const instruction =
		response.type === "RECORDED_AUDIO" ? (response.instruction ?? "") : "";

	const audioUri = extractAudioUri(content);
	const videoUri = extractVideoUri(content);
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest || showTimerConfig,
	);
	const initialElapsedSeconds = responseConfig?.overrideTestTimerWithItemTimer
		? (item.answer?.timeElapsed ?? 0)
		: 0;
	const prepTime = responseConfig?.prepTime;
	const allowAudioReplay = getAllowAudioReplay(responseConfig, isSummativeTest);
	const allowVideoReplay = getAllowVideoReplay(responseConfig, isSummativeTest);
	const videoPlayerConfig = getVideoPlayerConfig(
		responseConfig,
		isSummativeTest,
	);
	const progressSeekable = getVideoProgressSeekable(isSummativeTest);
	const images = useMemo(
		() =>
			content.stimulus?.type === "IMAGE"
				? (content.stimulus.media?.map((m) => ({
						id: String(m.id),
						url: m.url,
					})) ?? [])
				: [],
		[content.stimulus],
	);
	const stem =
		content.stimulus?.type === "TEXT"
			? (content.stimulus.text ?? "")
			: (content.stem?.text ?? "");

	const disabled = streaming || isAnswered;
	const recorderProps: RecorderProps = useMemo(
		() => ({
			audio,
			handleOnRecordingComplete,
			captureError,
			handleTracking,
			disabled,
			prepTime,
			timeLimit,
			initialElapsedSeconds,
			allowAudioReplay,
			allowVideoReplay,
			videoPlayerConfig,
			progressSeekable,
			allowStopWhileRecording: !isSummativeTest,
			onResponseComplete: handleResponseComplete,
			onAudioEnded: handleAudioEnded,
			registerActiveResponseCompletion: handleRegisterActiveResponseCompletion,
			onLimitReachedChange: handleLimitReachedChange,
			onRecordingActiveChange: handleRecordingActiveChange,
			onPlaybackDurationChange: handlePlaybackDurationChange,
		}),
		[
			audio,
			handleOnRecordingComplete,
			handleTracking,
			disabled,
			prepTime,
			timeLimit,
			initialElapsedSeconds,
			allowAudioReplay,
			allowVideoReplay,
			videoPlayerConfig,
			progressSeekable,
			isSummativeTest,
			handleResponseComplete,
			handleAudioEnded,
			handleRegisterActiveResponseCompletion,
			handleLimitReachedChange,
			handleRecordingActiveChange,
			handlePlaybackDurationChange,
		],
	);

	return (
		<Box gap={16}>
			<SpeakingItemInput
				itemType={itemType}
				stem={stem}
				instruction={instruction}
				audioUri={audioUri}
				videoUri={videoUri}
				images={images}
				isSummativeTest={isSummativeTest}
				recorderProps={recorderProps}
			/>
			<FormativeTimeOverrunContainer />
			{isSummativeTest || showTimerConfig ? null : (
				<TimeOverrunBanner
					visible={recordingExpired && !expiredDismissed}
					onDismiss={() => setExpiredDismissed(true)}
				/>
			)}
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default SpeakingContainer;
